import Link from 'next/link'
import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { TeamMemberCardProps } from '~/components/atoms/TeamMemberCard/TeamMemberCard'
import TeamMemberCard from '~/components/atoms/TeamMemberCard/TeamMemberCard'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import { useSettingsContext } from '~/contexts/settingsContext'
import analyticsPusher, { analyticsEventTypes } from '~/utils/analyticsPusher'
import formatPhone from '~/utils/formatPhone'

import styles from './LeadershipTeamModule.module.css'

export interface LeadershipTeamModuleProps {
  teamMembers?: TeamMemberCardProps[]
  theme: ComponentTheme
  title?: string
  introText?: any
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  moduleId?: string
}

export default function LeadershipTeamModule({
  title,
  introText,
  teamMembers,
  theme = ComponentTheme.primary,
  verticalPadding,
  noPaddingBottom,
  moduleId,
}: LeadershipTeamModuleProps) {
  const t = useTranslations('leadershipTeamModule')
  const { mabAddress, mabPhone, mabAddressUS, mabPhoneUS, mabEmail } =
    useSettingsContext()

  return (
    <Fixture
      verticalPadding={verticalPadding}
      theme={theme}
      halfPadding
      noPaddingBottom={noPaddingBottom}
      id={moduleId}
    >
      <Container>
        <div
          className={classNames(
            styles.base,
            !verticalPadding &&
              verticalPadding !== undefined &&
              styles.baseNoPadding,
            noPaddingBottom && styles.baseNoPaddingBottom
          )}
        >
          <div className={classNames(styles.contentContainer)}>
            <div className={styles.introContainer}>
              {title && (
                <Text className={styles.title} size={TextSizes.primaryTitleLrg}>
                  <div dangerouslySetInnerHTML={{ __html: title }}></div>
                </Text>
              )}
              {introText && (
                <div className={classNames(styles.summary, 'prose')}>
                  <BlockContent value={introText} />
                </div>
              )}
            </div>
            <div className={styles.teamList}>
              {teamMembers &&
                teamMembers.map((item: TeamMemberCardProps, i: number) => (
                  <div
                    className={styles.listItem}
                    key={`report-team-member-item-${i}`}
                  >
                    <TeamMemberCard
                      showEmail={false}
                      showSummary
                      summarySmall
                      jobTitleBold
                      useVariant
                      showViewProfile
                      {...item}
                    />
                  </div>
                ))}
            </div>
            <div className={styles.contactSection}>
              {/* First Column */}
              <div
                className={classNames(styles.listItem, styles.listItemFirst)}
              >
                <Text
                  size={TextSizes.primaryTitleLrg}
                  className={styles.contactTitle}
                >
                  {t('contact')}
                </Text>
              </div>
              {/* Second column */}
              <div
                className={classNames(styles.listItem, styles.listItemSecond)}
              >
                {/* contact intro */}
                <Text
                  size={TextSizes.primaryTitleSml}
                  className={styles.contactAddressTitle}
                >
                  {t('byAppointmentOnly')}
                </Text>
                {/* Address section */}
                <BlockContent value={mabAddress}></BlockContent>
                {mabAddressUS && (
                  <div className={styles.mabAddressUS}>
                    <BlockContent value={mabAddressUS}></BlockContent>
                  </div>
                )}
                <div className={styles.listItemContact}>
                  <Text
                    size={TextSizes.bodyMed}
                    className={styles.footerContactText}
                  >
                    {t('teamAvailableForComment')} <br />
                    {t('contactPressOffice')}{' '}
                    <Link
                      href={'mailto:press@myartbroker.com'}
                      className={styles.link}
                    >
                      {t('pressEmail')}
                    </Link>
                  </Text>
                </div>
                {/* Mail section */}
                <div className={styles.contactDetails}>
                  <div className={classNames(styles.contactItem, styles.first)}>
                    <Text size={TextSizes.primaryTitleSml}>{t('callUs')}</Text>
                    <div className={styles.contactPhones}>
                      <Link
                        href={`tel:${formatPhone(mabPhone)}`}
                        className={classNames(styles.link, 'p-tel')}
                        onClick={() => {
                          analyticsPusher(analyticsEventTypes.gaEvent, {
                            gaEventName: 'Report: Phone link clicked',
                          })
                        }}
                      >
                        <Text>{mabPhone}</Text>
                      </Link>
                      {mabPhoneUS && (
                        <Link
                          href={`tel:${formatPhone(mabPhone)}`}
                          className={classNames(styles.link, 'p-tel')}
                          onClick={() => {
                            analyticsPusher(analyticsEventTypes.gaEvent, {
                              gaEventName: 'Report: US Phone link clicked',
                            })
                          }}
                        >
                          <Text>{mabPhoneUS}</Text>
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className={styles.contactItem}>
                    <Text size={TextSizes.primaryTitleSml}>{t('emailUs')}</Text>
                    <Link
                      href={`mailto:${mabEmail}`}
                      className={classNames(styles.link, 'u-email')}
                      onClick={() => {
                        analyticsPusher(analyticsEventTypes.gaEvent, {
                          gaEventName: 'Report: Mail link clicked',
                        })
                      }}
                    >
                      <Text>{mabEmail}</Text>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Fixture>
  )
}
